'use client'

import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuButton,
	useSidebar,
} from '@/components/ui/sidebar'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Link, useLocation } from 'react-router-dom'

import type { LucideIcon } from 'lucide-react'

export function NavSection({
	title,
	items,
}: {
	title?: string
	items: Array<{
		title: string
		url: string
		icon?: LucideIcon
		isActive?: boolean
		matchPattern?: string | ((pathname: string) => boolean)
		onClick?: (e: React.MouseEvent) => void
		tooltip?: string
		disabled?: boolean
		badge?: number
		unreadIndicator?: boolean
	}>
}) {
	const location = useLocation()
	const { state } = useSidebar()

	return (
		<SidebarGroup>
			{title && <SidebarGroupLabel>{title}</SidebarGroupLabel>}
			<SidebarMenu>
				{items.map((item) => {
					let isItemActive = false

					if (item.matchPattern) {
						if (typeof item.matchPattern === 'string') {
							// If matchPattern is a string, treat it as a regex
							const regex = new RegExp(item.matchPattern)
							isItemActive = regex.test(location.pathname)
						} else if (typeof item.matchPattern === 'function') {
							// If matchPattern is a function, call it with the pathname
							isItemActive = item.matchPattern(location.pathname)
						}
					} else {
						// Default behavior: exact match or ends with the last part of the URL
						isItemActive =
							location.pathname === item.url ||
							location.pathname.endsWith(item.url.split('/').pop() || '')
					}

					if (item.onClick) {
					return (
						<SidebarMenuButton
							tooltip={item.tooltip || item.title}
							key={item.title}
							className={cn(
								isItemActive && 'bg-primary text-primary-foreground',
								item.disabled && 'opacity-50 cursor-not-allowed hover:bg-muted'
							)}
							onClick={item.onClick}
						>
							{item.icon && (
								item.unreadIndicator && state === 'collapsed' ? (
									<div className="relative inline-flex">
										<item.icon className="h-4 w-4" />
										<div className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-rose-700 rounded-full"></div>
									</div>
								) : (
									<item.icon />
								)
							)}
							<span>{item.title}</span>
							{item.badge && item.badge > 0 && (
								<Badge variant="destructive" className="ml-auto h-5 w-5 p-0 flex items-center justify-center text-xs bg-rose-700 hover:bg-rose-700">
									{item.badge > 99 ? '99+' : item.badge}
								</Badge>
							)}
							{item.unreadIndicator && state === 'expanded' && (
								<div className="ml-auto h-2 w-2 bg-rose-700 rounded-full"></div>
							)}
						</SidebarMenuButton>
					)
					}

					if (item.disabled) {
						return (
							<SidebarMenuButton
								tooltip={item.tooltip}
								key={item.title}
								className={cn(
									'opacity-50 cursor-not-allowed hover:bg-muted hover:text-foreground pointer-events-auto'
								)}
								onClick={(e) => e.preventDefault()}
							>
								{item.icon && (
									item.unreadIndicator && state === 'collapsed' ? (
										<div className="relative inline-flex">
											<item.icon className="h-4 w-4" />
											<div className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-rose-700 rounded-full"></div>
										</div>
									) : (
										<item.icon />
									)
								)}
								<span>{item.title}</span>
								{item.badge && item.badge > 0 && (
								<Badge variant="destructive" className="ml-auto h-5 w-5 p-0 flex items-center justify-center text-xs bg-rose-700 hover:bg-rose-700">
									{item.badge > 99 ? '99+' : item.badge}
								</Badge>
								)}
								{item.unreadIndicator && state === 'expanded' && (
									<div className="ml-auto h-2 w-2 bg-rose-700 rounded-full"></div>
								)}
							</SidebarMenuButton>
						)
					}

					return (
						<SidebarMenuButton
							tooltip={item.tooltip || item.title}
							asChild
							key={item.title}
							className={cn(
								isItemActive && 'bg-primary text-primary-foreground'
							)}
						>
							<Link to={item.url}>
								{item.icon && (
									item.unreadIndicator && state === 'collapsed' ? (
										<div className="relative inline-flex">
											<item.icon className="h-4 w-4" />
											<div className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-rose-700 rounded-full"></div>
										</div>
									) : (
										<item.icon />
									)
								)}
								<span>{item.title}</span>
								{item.badge && item.badge > 0 && (
								<Badge variant="destructive" className="ml-auto h-5 w-5 p-0 flex items-center justify-center text-xs bg-rose-700 hover:bg-rose-700">
									{item.badge > 99 ? '99+' : item.badge}
								</Badge>
								)}
								{item.unreadIndicator && state === 'expanded' && (
									<div className="ml-auto h-2 w-2 bg-rose-700 rounded-full"></div>
								)}
							</Link>
						</SidebarMenuButton>
					)
				})}
			</SidebarMenu>
		</SidebarGroup>
	)
}
